import { isNil, sortBy } from 'lodash';
import { useContext, useMemo } from 'react';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useAllTagOptions } from '@shared/components/blocks/lib/tags/hooks';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface TagOption {
  id: string;
  value: string;
  label: string;
  backgroundColor: string;
  archived: boolean;
  fieldName: string;
  sortKey: string;
}

export interface UseTagOptionsResponse {
  options: TagOption[];
  refetch(): void;
  loading: boolean;
}

export function useWorkflowTagOptions(componentId: string): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const hasWorkflowTemplate = !isNil(channel?.workflowTemplateId);

  const { workflowTemplate, loading: wftLoading } = useWorkflowTemplate(
    hasWorkflowTemplate ? channel?.workflowTemplateId : undefined,
  );

  const {
    options: allOptions,
    loading: allTagsLoading,
    refetch,
  } = useAllTagOptions(hasWorkflowTemplate);

  const options = useMemo(() => {
    if (hasWorkflowTemplate) {
      const component = workflowTemplate?.components?.find(
        (c): c is API.TagComponent => isTagComponent(c) && c.id === componentId,
      );
      return (
        component?.options?.map(
          (option): TagOption => ({
            id: option.value,
            value: option.value,
            label: option.label,
            backgroundColor: option.backgroundColor ?? '',
            archived: option.archived ?? false,
            fieldName: componentId,
            sortKey: option.value,
          }),
        ) ?? []
      );
    }
    const filtered = allOptions.filter(tag => tag.fieldName === componentId);
    return sortBy(filtered, tag => tag.archived);
  }, [hasWorkflowTemplate, workflowTemplate, componentId, allOptions]);

  const loading = hasWorkflowTemplate ? wftLoading : allTagsLoading;

  return { options, loading, refetch };
}

export function useCombinedTagOptions(componentIds: string[]): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const hasWorkflowTemplate = !isNil(channel?.workflowTemplateId);

  const { workflowTemplate, loading: wftLoading } = useWorkflowTemplate(
    hasWorkflowTemplate ? channel?.workflowTemplateId : undefined,
  );

  const {
    options: allOptions,
    loading: allTagsLoading,
    refetch,
  } = useAllTagOptions(hasWorkflowTemplate);

  const options = useMemo(() => {
    if (hasWorkflowTemplate) {
      return componentIds.flatMap(componentId => {
        const component = workflowTemplate?.components?.find(
          (c): c is API.TagComponent => isTagComponent(c) && c.id === componentId,
        );
        return (
          component?.options?.map(
            (option): TagOption => ({
              id: option.value,
              value: option.value,
              label: option.label,
              backgroundColor: option.backgroundColor ?? '',
              archived: option.archived ?? false,
              fieldName: componentId,
              sortKey: option.value,
            }),
          ) ?? []
        );
      });
    }
    const filtered = allOptions.filter(tag => componentIds.includes(tag.fieldName));
    return sortBy(filtered, tag => tag.archived);
  }, [hasWorkflowTemplate, workflowTemplate, componentIds, allOptions]);

  const loading = hasWorkflowTemplate ? wftLoading : allTagsLoading;

  return { options, loading, refetch };
}
